import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Modal, Pagination } from '@/components/molecules';
import { ReturnForm } from '@/components/organisms';
import { SalesOrderDetailModal } from '@/components/organisms/Sales';
import { useSalesList, useUpdateSaleStatusMutation, useInvoiceRequirementsQuery } from '@/hooks/useErpApi';
import { erpClient } from '@/services/clients';
import type { SaleResponse } from '@kisanlink/erp-service';

// Sale status types matching API
type SaleStatus = 'pending' | 'completed' | 'cancelled';

// Status flow: pending → completed (no confirmed status)
const STATUS_FLOW: SaleStatus[] = ['pending', 'completed'];

interface Sale {
  id: string;
  orderId: string;
  customer: string;
  phoneNumber: string | null;
  isOrgMember: boolean;
  amount: number;
  status: SaleStatus;
  date: string;
}

export default function SalesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | SaleStatus>('all');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<SaleResponse | null>(null);
  const [returnSaleId, setReturnSaleId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const { data: sales, isLoading, error, refetch } = useSalesList({
    limit: itemsPerPage,
    offset,
    ...(activeTab !== 'all' && { status: activeTab }),
  });
  const { mutateAsync: updateSaleStatus } = useUpdateSaleStatusMutation();
  
  // Check invoice requirements
  const { data: invoiceRequirements, isLoading: isLoadingRequirements } = useInvoiceRequirementsQuery();

  // Transform API data to UI format
  const allSales: Sale[] = useMemo(() => {
    return (sales || []).map((sale) => {
      const totalAmount = sale.total_amount || 0;
      const saleDate = sale.sale_date || sale.created_at;

      // Map API status to UI status - use actual status from API
      const status = (sale.status as SaleStatus) || 'pending';

      return {
        id: sale.id,
        orderId: sale.id,
        customer: sale.customer_name || 'Walk-in Customer',
        phoneNumber: sale.customer_phone || null,
        isOrgMember: sale.is_org_member || false,
        amount: totalAmount,
        status,
        date: saleDate,
      };
    });
  }, [sales]);

  // Get total count from API response if available, otherwise use current data length
  const totalSales = (sales as any)?.total_count || (sales as any)?.length || allSales.length;
  
  // Reset to page 1 when tab changes
  const handleTabChange = (tab: 'all' | SaleStatus) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const getStatusBadge = (status: SaleStatus) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'cancelled':
        return <Badge variant="error">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get the next status in the flow
  const getNextStatus = (currentStatus: SaleStatus): SaleStatus | null => {
    const currentIndex = STATUS_FLOW.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex === STATUS_FLOW.length - 1) return null;
    return STATUS_FLOW[currentIndex + 1];
  };

  // Handle advancing to next status
  const handleAdvanceStatus = async (saleId: string, currentStatus: SaleStatus) => {
    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) return;

    setUpdatingStatusId(saleId);
    try {
      await updateSaleStatus({ id: saleId, status: nextStatus });
      refetch();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Handle cancelling a sale
  const handleCancelSale = async (saleId: string) => {
    if (!window.confirm('Are you sure you want to cancel this sale?')) return;

    setUpdatingStatusId(saleId);
    try {
      await updateSaleStatus({ id: saleId, status: 'cancelled' });
      refetch();
    } catch (err) {
      console.error('Failed to cancel sale:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const totalRevenue = allSales.reduce((sum, sale) => sum + sale.amount, 0);
  const completedSales = allSales.filter((s) => s.status === 'completed').length;
  const pendingSales = allSales.filter((s) => s.status === 'pending').length;

  const handleCreateSale = () => {
    navigate('/sales/create');
  };

  const handleViewSale = async (saleId: string) => {
    try {
      // Fetch full sale details (includes items and breakdown)
      const response = await erpClient.sales.get(saleId);
      if (response?.data) {
        setSelectedSale(response.data);
        setIsDetailModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to fetch sale details:', err);
      // Fallback: try to use list data (may not have items/breakdown)
      const sale = sales?.find((s) => s.id === saleId);
      if (sale) {
        setSelectedSale(sale as SaleResponse);
        setIsDetailModalOpen(true);
      }
    }
  };

  const handleDownloadInvoice = async (saleId: string) => {
    // Wait for requirements to load if still loading
    if (isLoadingRequirements) {
      alert('Please wait while we check invoice requirements...');
      return;
    }

    // Check if invoice requirements are met
    // API returns: { ready: true, missing_settings: null } when ready
    // API returns: { ready: false, missing_settings: [...] } when not ready
    const requirementsData = (invoiceRequirements as any)?.data;
    const isReady = requirementsData?.ready === true;
    
    // Only show dialog if explicitly not ready AND has missing settings
    if (requirementsData && !isReady) {
      const missing = Array.isArray(requirementsData.missing_settings) 
        ? requirementsData.missing_settings 
        : [];
      
      if (missing.length > 0) {
        const missingList = missing
          .map((s: string) => s.replace('fpo_', '').replace(/_/g, ' '))
          .join(', ');
        
        if (window.confirm(
          `Invoice requirements not met. Missing: ${missingList}\n\nWould you like to configure settings now?`
        )) {
          navigate('/settings');
        }
        return;
      }
    }

    // Proceed with download (either ready=true or data not loaded yet - backend will validate)
    setDownloadingInvoiceId(saleId);

    try {
      // Generate invoice PDF
      const blob = await (erpClient as any).sales.generateInvoice(saleId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Extract sale number for filename
      // Invoice numbers are now 12 characters (MMYYXXXXXXXX), but fallback handles both old (8) and new (12) formats
      const sale = sales?.find((s) => s.id === saleId);
      const saleNumber = (sale as any)?.invoice_number || saleId.substring(0, 12);
      a.download = `Invoice_${saleNumber}.pdf`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download invoice';
      console.error('Invoice download error:', err);
      
      // Show error to user
      alert(`Failed to download invoice: ${errorMessage}`);
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleEditSale = (saleId: string) => {
    const sale = sales?.find((s) => s.id === saleId);
    if (sale) {
      setSelectedSale(sale as SaleResponse);
      setIsDetailModalOpen(true);
    }
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedSale(null);
  };

  const handleFormSuccess = () => {
    setIsDetailModalOpen(false);
    setSelectedSale(null);
    refetch();
  };

  const handleCreateReturn = (saleId: string) => {
    setReturnSaleId(saleId);
    setIsReturnModalOpen(true);
  };

  const handleReturnSuccess = () => {
    setIsReturnModalOpen(false);
    setReturnSaleId(null);
    navigate('/returns');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="ShoppingCart" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading sales...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="TriangleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">Failed to Load Sales</h3>
              <p className="text-error-700 mb-3">Unable to fetch sales data. Please try again.</p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Sales</h1>
          <p className="text-neutral-600 mt-2">Track and manage your sales transactions</p>
        </div>
        <Button variant="primary" className="sm:w-auto" onClick={handleCreateSale}>
          <Icon name="Plus" size={20} className="mr-2" />
          New Sale Order
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Total Sales</p>
            <div className="bg-primary-50 p-2 rounded-lg">
              <Icon name="ShoppingCart" size={20} className="text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{allSales.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Completed</p>
            <div className="bg-success-50 p-2 rounded-lg">
              <Icon name="CircleCheck" size={20} className="text-success-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-success-700">{completedSales}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Pending</p>
            <div className="bg-warning-50 p-2 rounded-lg">
              <Icon name="Clock" size={20} className="text-warning-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-warning-700">{pendingSales}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Revenue</p>
            <div className="bg-info-50 p-2 rounded-lg">
              <Icon name="IndianRupee" size={20} className="text-info-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">
            ₹{(totalRevenue / 100000).toFixed(1)}L
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="flex gap-2 flex-wrap">
          {(['all', 'pending', 'completed', 'cancelled'] as const).map((tab: 'all' | SaleStatus) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab as 'all' | SaleStatus)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                activeTab === tab
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab !== 'all' && (
                <span className="ml-2 text-xs opacity-75">
                  (
                  {tab === 'pending'
                    ? pendingSales
                    : tab === 'completed'
                      ? completedSales
                      : allSales.filter((s) => s.status === 'cancelled').length}
                  )
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[140px]">
                  Order ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[180px]">
                  Customer
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[140px]">
                  Phone Number
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[120px]">
                  Org Member
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[120px]">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[120px]">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[140px]">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-neutral-700 uppercase tracking-wider min-w-[180px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {allSales.map((sale) => (
                <tr key={sale.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-neutral-900 font-mono whitespace-nowrap">
                      {sale.orderId}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center min-w-0">
                      <div className="h-8 w-8 flex-shrink-0 bg-info-100 rounded-full flex items-center justify-center">
                        <Icon name="Building" size={16} className="text-info-600" />
                      </div>
                      <div className="ml-3 min-w-0 flex-1">
                        <div className="text-sm font-medium text-neutral-900 break-words">{sale.customer}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-600 whitespace-nowrap">
                      {sale.phoneNumber || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {sale.isOrgMember ? (
                        <Badge variant="success" size="sm">FPO Member</Badge>
                      ) : (
                        <span className="text-sm text-neutral-500">No</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-bold text-neutral-900 whitespace-nowrap">
                      ₹{sale.amount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-neutral-600 whitespace-nowrap">
                      {new Date(sale.date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                      {getStatusBadge(sale.status)}
                      {/* Quick status actions */}
                      {sale.status !== 'completed' && sale.status !== 'cancelled' && (
                        <div className="flex items-center gap-1 ml-2">
                          {/* Advance status button */}
                          <button
                            onClick={() => handleAdvanceStatus(sale.id, sale.status)}
                            disabled={updatingStatusId === sale.id}
                            className="p-1 rounded text-success-600 hover:text-success-700 hover:bg-success-50 transition-colors disabled:opacity-50"
                            title={`Mark as ${getNextStatus(sale.status) || 'next'}`}
                          >
                            {updatingStatusId === sale.id ? (
                              <Icon name="Loader" size={14} className="animate-spin" />
                            ) : (
                              <Icon name="Check" size={14} />
                            )}
                          </button>
                          {/* Cancel button */}
                          <button
                            onClick={() => handleCancelSale(sale.id)}
                            disabled={updatingStatusId === sale.id}
                            className="p-1 rounded text-error-600 hover:text-error-700 hover:bg-error-50 transition-colors disabled:opacity-50"
                            title="Cancel sale"
                          >
                            <Icon name="X" size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                      <button
                        onClick={() => handleViewSale(sale.id)}
                        className="text-primary-600 hover:text-primary-900 p-2 rounded-lg hover:bg-primary-50 transition-colors"
                        title="View Sale"
                      >
                        <Icon name="Eye" size={16} />
                      </button>
                      <button
                        onClick={() => handleEditSale(sale.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          sale.status === 'completed' || sale.status === 'cancelled'
                            ? 'text-neutral-400 cursor-not-allowed'
                            : 'text-info-600 hover:text-info-900 hover:bg-info-50'
                        }`}
                        title={
                          sale.status === 'completed' || sale.status === 'cancelled'
                            ? 'Cannot edit completed or cancelled sales'
                            : 'Edit Sale'
                        }
                        disabled={sale.status === 'completed' || sale.status === 'cancelled'}
                      >
                        <Icon name="Pencil" size={16} />
                      </button>
                      <button
                        onClick={() => handleCreateReturn(sale.id)}
                        className="text-warning-600 hover:text-warning-900 p-2 rounded-lg hover:bg-warning-50 transition-colors"
                        title="Create Return"
                      >
                        <Icon name="RefreshCw" size={16} />
                      </button>
                      <button
                        onClick={() => handleDownloadInvoice(sale.id)}
                        disabled={downloadingInvoiceId === sale.id || isLoadingRequirements}
                        className={`p-2 rounded-lg transition-colors ${
                          downloadingInvoiceId === sale.id || isLoadingRequirements
                            ? 'text-neutral-400 cursor-wait'
                            : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                        }`}
                        title={
                          downloadingInvoiceId === sale.id
                            ? 'Downloading...'
                            : isLoadingRequirements
                              ? 'Checking requirements...'
                              : 'Download Invoice'
                        }
                      >
                        {downloadingInvoiceId === sale.id || isLoadingRequirements ? (
                          <Icon name="Loader" size={16} className="animate-spin" />
                        ) : (
                          <Icon name="Download" size={16} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalSales}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          disabled={isLoading}
        />
      </div>

      {/* Recent Activity */}
      <div className="bg-gradient-to-br from-success-600 to-success-700 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Today&apos;s Performance</h3>
          <Icon name="TrendingUp" size={24} className="text-success-200" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-success-100 text-sm">Orders Today</p>
            <p className="text-2xl font-bold mt-1">12</p>
          </div>
          <div>
            <p className="text-success-100 text-sm">Revenue Today</p>
            <p className="text-2xl font-bold mt-1">₹2.8L</p>
          </div>
          <div>
            <p className="text-success-100 text-sm">Avg Order Value</p>
            <p className="text-2xl font-bold mt-1">₹23,333</p>
          </div>
        </div>
      </div>

      {/* Sales Order Detail Modal */}
      <SalesOrderDetailModal
        sale={selectedSale}
        open={isDetailModalOpen}
        onClose={handleDetailModalClose}
        onSuccess={handleFormSuccess}
      />

      {/* Create Return Modal */}
      <Modal
        isOpen={isReturnModalOpen}
        onClose={() => {
          setIsReturnModalOpen(false);
          setReturnSaleId(null);
        }}
        title="Create Return"
        size="lg"
      >
        <ReturnForm
          saleId={returnSaleId || undefined}
          onSuccess={handleReturnSuccess}
          onCancel={() => {
            setIsReturnModalOpen(false);
            setReturnSaleId(null);
          }}
        />
      </Modal>
    </div>
  );
}
